import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle, ArrowLeft, KeyRound } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../Logo';
import { toast } from 'sonner';

export const ForgotPasswordPage = ({ onBack }) => {
  const { forgotPassword, resetPassword } = useAuth();
  const [step, setStep] = useState('request'); // request | reset | done
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      const data = await forgotPassword(email);
      if (data.resetToken) {
        setResetToken(data.resetToken);
      }
      setStep('reset');
      toast.success('Reset instructions sent!');
    } catch (err) {
      setError(err.message);
    }
    setIsLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!resetToken.trim()) {
      setError('Please enter the reset token');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setIsLoading(true);
    try {
      await resetPassword(resetToken, newPassword);
      setStep('done');
      toast.success('Password reset successfully!');
    } catch (err) {
      setError(err.message);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#070b14] flex items-center justify-center px-4 dark" data-testid="forgot-password-page">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md">
        <div className="text-center mb-8 flex justify-center">
          <Logo size="large" />
        </div>

        <div className="p-8 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl">
          {step === 'done' ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Password Reset Successful</h3>
              <p className="text-gray-400 text-sm mb-6">You can now sign in with your new password.</p>
              <button onClick={onBack}
                className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity"
                data-testid="back-to-login-btn">
                Back to Sign In
              </button>
            </div>
          ) : step === 'reset' ? (
            <form onSubmit={handleResetPassword} className="space-y-5">
              {error && (
                <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
                </motion.div>
              )}

              {resetToken && (
                <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-sm">
                  <p className="text-blue-400 text-xs font-medium mb-1">Reset Token (auto-filled for demo)</p>
                  <p className="text-gray-400 text-xs font-mono break-all">{resetToken.substring(0, 40)}...</p>
                </div>
              )}

              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Reset Token</label>
                <div className="relative">
                  <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="text" value={resetToken} onChange={(e) => setResetToken(e.target.value)}
                    placeholder="Paste your reset token"
                    className="w-full h-11 pl-11 pr-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all font-mono text-xs"
                    data-testid="reset-token-input" />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min. 6 characters"
                    className="w-full h-11 pl-11 pr-11 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all"
                    data-testid="new-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={isLoading}
                className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="reset-password-btn">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRequestReset} className="space-y-5">
              {error && (
                <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
                </motion.div>
              )}

              <p className="text-gray-400 text-sm">Enter the email associated with your account and we'll send reset instructions.</p>

              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                    className="w-full h-11 pl-11 pr-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all"
                    data-testid="forgot-email-input" />
                </div>
              </div>

              <button type="submit" disabled={isLoading}
                className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="send-reset-btn">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Instructions'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-gray-400 mt-6">
          <button onClick={onBack} className="text-blue-400 hover:text-blue-300 font-medium transition-colors flex items-center gap-1 mx-auto" data-testid="back-to-login-link">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to sign in
          </button>
        </p>
      </motion.div>
    </div>
  );
};
